import { createSlice } from '@reduxjs/toolkit';
import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
// utils
import axios from '../../utils/axios';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  leads: [],
  remindedLeads: [],
  lead: null,
  accountsCreated: [],
  category: null,
  other: null
};

const slice = createSlice({
  name: 'lead',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET LEADS
    getLeadsSuccess(state, action) {
      state.isLoading = false;
      state.leads = action.payload;
    },

    // GET REMINDED LEADS
    getRemindedLeadsSuccess(state, action) {
      state.isLoading = false;
      const remindedLeads = action.payload;
      remindedLeads.forEach(leadId => {
        const leadIndex = state.leads.findIndex(lead => lead.leadId === leadId);
        if (leadIndex !== -1) {
          state.leads[leadIndex].remindedIn24hrs = true;
          state.leads[leadIndex].reminded = true;
        }
      });
    },

    // CREATE ACCOUNT
    createAccountSuccess(state, action) {
      state.isLoading = false;
      state.accountsCreated = action.payload;
    },

    // UPDATE LEAD
    updateLeadSuccess(state, action) {
      state.isLoading = false;
      if (action.payload.error) {
        state.error = action.payload.message
      } else {
        const lead_data = action.payload;
        const leadIndex = state.leads.findIndex(lead => lead.leadId === lead_data.leadId);
        if (leadIndex !== -1) {
          state.leads[leadIndex] = lead_data;
        }
      }
    },

    // GET LEAD
    getLeadSuccess(state, action) {
      state.isLoading = false;
      state.lead = action.payload;
    },

    // UPDATE LEAD STATUS
    updateLeadsStatusSuccess(state, action) {
      state.isLoading = false;
      if (action.payload.error) {
        state.error = action.payload.message
      } else {
        const { leadIds, status } = action.payload;
        leadIds.forEach(leadId => {
          const leadIndex = state.leads.findIndex(lead => lead.leadId === leadId);
          if (leadIndex !== -1) {
            state.leads[leadIndex].status = status;
          }
        });
      }
    },

    // UPDATE LEAD STATUS
    sendActivationReminderSuccess(state, action) {
      state.isLoading = false;
      if (action.payload.error) {
        state.error = action.payload.message
      } else {
        const { leadIds } = action.payload;
        leadIds.forEach(leadId => {
          const leadIndex = state.leads.findIndex(lead => lead.leadId === leadId);
          if (leadIndex !== -1) {
            state.leads[leadIndex].reminded = true;
            state.leads[leadIndex].remindedIn24hrs = true;
          }
        });
      }
    },


    // UPDATE LEAD STATUS
    updateVerificationSuccess(state, action) {
      state.isLoading = false;
      if (action.payload.error) {
        state.error = action.payload.message
      } else {
        const { leadId, verified } = action.payload;
        const leadIndex = state.leads.findIndex(lead => lead.leadId === leadId);
        if (leadIndex !== -1) {
          state.leads[leadIndex].verified = verified;
        }
      }
    },

    // UPDATE BOOKMARK STATUS
    updateBookmarkSuccess(state, action) {
      state.isLoading = false;
      if (action.payload.error) {
        state.error = action.payload.message
      } else {
        const { leadIds, bookmarked } = action.payload;
        leadIds.forEach(leadId => {
          const leadIndex = state.leads.findIndex(lead => lead.leadId === leadId);
          if (leadIndex !== -1) {
            state.leads[leadIndex].bookmarked = bookmarked;
          }
        });
      }
    },

    // UPDATE LEAD STATUS
    inviteLeadsSuccess(state, action) {
      state.isLoading = false;
      if (action.payload.error) {
        state.error = action.payload.message
      } else {
        const { leadIds, status, invited } = action.payload;
        leadIds.forEach(leadId => {
          const leadIndex = state.leads.findIndex(lead => lead.leadId === leadId);
          if (leadIndex !== -1) {
            state.leads[leadIndex].status = status;
          }
        });
      }
    },

    //  SORT & FILTER PRODUCTS
    sortByLeads(state, action) {
      state.sortBy = action.payload;
    },

  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  sortByLeads,
} = slice.actions;

// ----------------------------------------------------------------------

export function getLeads() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/account/leads/get_active_leads');
      dispatch(slice.actions.getLeadsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getRemindedLeads() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/account/leads/get_reminded_leads');
      dispatch(slice.actions.getRemindedLeadsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getLead(leadId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/account/leads/get_lead', {leadId});
      dispatch(slice.actions.getLeadSuccess(response.data.lead));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function createAccount(users, account, locations) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/account/manage/create', {'users': users, 'account': account, 'locations': locations});
      dispatch(slice.actions.createAccountSuccess(response.data.accounts));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function updateLead(leadId, data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/account/leads/update_lead', {leadId, data});
      dispatch(slice.actions.updateLeadSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function updateLeadsStatus(leadIds, status) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/account/leads/update_status', {leadIds, status});
      dispatch(slice.actions.updateLeadsStatusSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function updateVerificationStatus(leadId, verified) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/account/leads/update_verification_status', {leadId, verified});
      dispatch(slice.actions.updateVerificationSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function sendActivationReminder(leadIds) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/account/leads/send_activation_reminder', {leadIds});
      dispatch(slice.actions.sendActivationReminderSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function updateBookmark(leadIds, bookmarked) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/account/leads/add_bookmark', {leadIds});
      dispatch(slice.actions.updateBookmarkSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function createAccountAndSendInvite(leadIds) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/account/leads/create_account_and_send_invite', {leadIds});
      dispatch(slice.actions.inviteLeadsSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

